import { useEffect } from 'react';
import { Box } from '@nimbus-ds/components';
import { PlusCircleIcon } from '@nimbus-ds/icons';
import { FEATURE_ALLOW_CUSTOM_DOMAIN } from 'App/features';
import HelpLink from 'App/HelpLink';
import { useNavegate, useQuery } from 'App/hooks';
import {
  ActionProp,
  ActionsDesktop,
  ErrorScreen,
  HeaderContent,
  HeaderTop,
  IonPageStratus,
  useResponsive,
} from 'commons/components';
import { HELP_LINKS_DOMAINS } from 'commons/constants';
import { useModal, useTranslationLanguage } from 'commons/hooks';
import { DOMAINS_ADD_DOMAIN_BTN } from 'config/upsellFlowSources';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import { configurationsRoutes } from 'domains/Configurations';
import {
  useGetDomainsQuery,
  useUpdateOurDomainMutation,
  useGetActiveDomainOrderQuery,
} from 'domains/Configurations/Domains/domainsApi';
import { trackingAddDomainClick } from 'domains/Configurations/Domains/tracking';
import { UPDATE_OUR_DOMAIN_MUTATION_CACHE_KEY } from 'domains/Configurations/Domains/utils';
import useTranslationConfigurations from 'domains/Configurations/useTranslationConfigurations';
import {
  HelpSection,
  DomainsList,
  ActivateSSLAlert,
  ConfiguringDomainAlert,
  DomainUpdatedSuccessAlert,
  AddDomainModal as AddExistingDomainModal,
} from './components';
import { useAddDomainModalLogic } from './hooks';
import { SomeDomainHasErrorAlert } from '../../components';
import {
  DOMAIN_ORDER_PROCESSING_STATUSES,
  DOMAIN_ORDER_ERROR_STATUSES,
} from '../../constants';
import {
  AddDomainModal as PurchaseDomainModal,
  OrderProcessingAlert,
  OrderErrorAlert,
} from '../DomainsLandingPage/components';

export function DomainsListPage() {
  const t = useTranslationConfigurations('domains');
  const showUpdatedAlertParam = useQuery().get('showUpdatedAlert');
  const language = useTranslationLanguage();
  const { isMobile } = useResponsive();
  const { goTo } = useNavegate();

  const { data, isLoading, isError, refetch, isFetching } =
    useGetDomainsQuery();

  const {
    hasNuvemDomainEnabled,
    showAddDomainModal,
    showPurchaseDomainModal,
    initialSearchQuery,
    setInitialSearchQuery,
    openAddDomainModal,
    openPurchaseDomainModal,
    closeAddDomainModal,
    closePurchaseDomainModal,
    handleOpenModal,
  } = useAddDomainModalLogic();

  const { data: activeOrder } = useGetActiveDomainOrderQuery(undefined, {
    skip: !hasNuvemDomainEnabled,
  });

  const [
    showDomainUpdatedSuccessAlert,
    openDomainUpdatedAlert,
    closeDomainUpdatedAlert,
  ] = useModal(false);

  const [, { isSuccess: ourDomainUpdated, data: ourDomainData }] =
    useUpdateOurDomainMutation({
      fixedCacheKey: UPDATE_OUR_DOMAIN_MUTATION_CACHE_KEY,
    });

  const handleRetryPayment = () => {
    if (activeOrder) {
      setInitialSearchQuery(activeOrder.domainName);
      openPurchaseDomainModal();
    }
  };

  const handleRedirectToActiveOrder = () => {
    if (activeOrder) {
      goTo(
        configurationsRoutes.domainsOrder.replace(
          ':payOrderId',
          activeOrder.payOrderId,
        ),
      );
    }
  };

  const showProcessingAlert =
    hasNuvemDomainEnabled &&
    activeOrder &&
    DOMAIN_ORDER_PROCESSING_STATUSES.includes(activeOrder.status);

  const showErrorAlert =
    hasNuvemDomainEnabled &&
    activeOrder &&
    DOMAIN_ORDER_ERROR_STATUSES.includes(activeOrder.status);

  const handleOpenAddDomainModal = useUpsellFlow({
    title: t('upsell.addDomain'),
    featureKey: FEATURE_ALLOW_CUSTOM_DOMAIN,
    callback: () => {
      trackingAddDomainClick();
      handleOpenModal();
    },
    trackingSource: DOMAINS_ADD_DOMAIN_BTN,
  });

  const addAction: ActionProp = {
    children: t('addBtn'),
    icon: PlusCircleIcon,
    onClick: handleOpenAddDomainModal,
    appearance: isMobile ? undefined : 'primary',
  };

  const showAddButton = !isError || isLoading || isFetching;

  const showActiveSSLAlert =
    !!data &&
    data.some(
      (domain) =>
        domain.status.domain === 'ok' &&
        domain.status.ssl === 'domain_not_ready',
    );

  useEffect(() => {
    if (ourDomainUpdated || !!showUpdatedAlertParam) openDomainUpdatedAlert();
  }, [ourDomainUpdated, openDomainUpdatedAlert, showUpdatedAlertParam]);

  const ourDomainName = ourDomainData?.new_url || data?.[0].name || '';

  return (
    <IonPageStratus
      width="medium"
      headerTop={
        <HeaderTop mainAction={showAddButton ? addAction : undefined} />
      }
      headerContent={
        <HeaderContent
          title={t('title')}
          subtitle={t('subtitle')}
          actions={showAddButton && <ActionsDesktop actions={[addAction]} />}
        />
      }
    >
      {isError && <ErrorScreen onRetry={refetch} />}
      {!isError && (
        <>
          <SomeDomainHasErrorAlert showLink={false} isRemovable={false} />
          {showDomainUpdatedSuccessAlert && !!ourDomainName && (
            <DomainUpdatedSuccessAlert
              domainName={ourDomainName}
              onRemove={closeDomainUpdatedAlert}
            />
          )}
          {showProcessingAlert && activeOrder && (
            <OrderProcessingAlert domainName={activeOrder.domainName} />
          )}
          {showErrorAlert && activeOrder && (
            <OrderErrorAlert
              domainName={activeOrder.domainName}
              payOrderId={activeOrder.payOrderId}
              onRetry={handleRetryPayment}
            />
          )}
          <ConfiguringDomainAlert domains={data} />
          {showActiveSSLAlert && <ActivateSSLAlert />}
          <Box
            display="flex"
            flexDirection="column"
            alignItems="stretch"
            gap="8"
            marginTop="3"
          >
            <DomainsList domains={data} isLoading={isLoading || isFetching} />
            <HelpSection />
          </Box>
          {hasNuvemDomainEnabled ? (
            <>
              {showPurchaseDomainModal && (
                <PurchaseDomainModal
                  onClose={closePurchaseDomainModal}
                  onOpenAddExistingDomain={openAddDomainModal}
                  initialSearchQuery={initialSearchQuery}
                  hasActiveProcessingOrder={Boolean(showProcessingAlert)}
                  onRedirectToActiveOrder={handleRedirectToActiveOrder}
                />
              )}
              <AddExistingDomainModal
                show={showAddDomainModal}
                onClose={closeAddDomainModal}
                source="list"
              />
            </>
          ) : (
            <AddExistingDomainModal
              show={showAddDomainModal}
              onClose={closeAddDomainModal}
            />
          )}
          <HelpLink
            title={t('helpLink.title')}
            previousValue=""
            currentViewTracking={t('helpLink.currentViewTracking')}
            linkURL={HELP_LINKS_DOMAINS[language]}
            icon="both"
            hasDisclaimer={false}
          />
        </>
      )}
    </IonPageStratus>
  );
}
